import React from 'react';
import styles from './styles.module.css';
import mainStyles from '../WorkerManagementComponent/WorkerManagementComponent.module.css';
import {
  Container,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Backdrop,
  CircularProgress,
  Snackbar,
  Tooltip,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import Pagination from '@material-ui/lab/Pagination';
import { Pages } from '../../models/Pages';
import { AuditLogEntry, IAuditLogEntry } from '../../models/Actions/AuditLog';
import { ActionsAuditActions, IAxiosAuditLog } from '../../actions/ActionsAuditActions';

interface IState {
  entries: AuditLogEntry[];
  pagination: Pages | null;
  backdrop: boolean;
  errorAlert: boolean;
  errors: string[];
  filter: AuditLogFilter;
}

class AuditLogFilter {
  action?: string;
  entity_type?: string;
  actor_id?: string;
  from?: string;
  to?: string;
  page?: string;
  per_page: string = '50';

  toString(): string {
    const arr: string[] = [];
    if (this.page) arr.push('page=' + this.page);
    arr.push('per_page=' + this.per_page);
    if (this.action) arr.push('action=' + encodeURIComponent(this.action));
    if (this.entity_type) arr.push('entity_type=' + encodeURIComponent(this.entity_type));
    if (this.actor_id) arr.push('actor_id=' + this.actor_id);
    if (this.from) arr.push('from=' + encodeURIComponent(this.from + ' 00:00:00'));
    if (this.to) arr.push('to=' + encodeURIComponent(this.to + ' 23:59:59'));
    return arr.join('&');
  }
}

export default class ActionsComponent extends React.Component<any, IState> {
  state: IState = {
    entries: [],
    pagination: null,
    backdrop: true,
    errorAlert: false,
    errors: [],
    filter: new AuditLogFilter(),
  };

  componentDidMount() {
    document.title = 'Actions';
    this.loadAuditLog();
  }

  loadAuditLog() {
    this.setState({ backdrop: true });
    ActionsAuditActions.getAuditLog(this.state.filter.toString())
      .then((data: IAxiosAuditLog) => {
        const entries = data.data.data.map((d: IAuditLogEntry) => new AuditLogEntry(d));
        const pages = new Pages(data.data.per_page, data.data.current_page, data.data.last_page);
        this.setState({ entries, pagination: pages, backdrop: false });
      })
      .catch((err: any) => {
        const errors = err?.response?.data?.errors ?? [err?.response?.data?.error ?? 'Unknown error'];
        this.setState({ errorAlert: true, backdrop: false, errors });
      });
  }

  handleChangePage(page: number) {
    const filter = this.state.filter;
    filter.page = page.toString();
    this.setState({ filter }, () => this.loadAuditLog());
  }

  handleFilter(field: keyof AuditLogFilter, value: string) {
    const filter = this.state.filter;
    (filter as any)[field] = value || undefined;
    filter.page = undefined;
    this.setState({ filter }, () => this.loadAuditLog());
  }

  truncate(str: string | null, max: number): string {
    if (!str) return '—';
    return str.length > max ? str.substring(0, max) + '…' : str;
  }

  actionBadgeClass(action: string): string {
    if (action.endsWith('_deleted')) return `${styles.ActionBadge} ${styles.ActionBadgeDeleted}`;
    if (action.endsWith('_updated')) return `${styles.ActionBadge} ${styles.ActionBadgeUpdated}`;
    return styles.ActionBadge;
  }

  parseTarget(targetUserName: string | null): { type: string; id: string } | null {
    if (!targetUserName) return null;
    const parts = targetUserName.split(':');
    if (parts.length >= 2) {
      return { type: parts[0], id: parts.slice(1).join(':') };
    }
    return { type: '', id: targetUserName };
  }

  render() {
    const { entries, pagination, filter } = this.state;

    return (
      <div>
        <Container maxWidth="lg">
          <Grid container spacing={1}>
            <Grid item xs={12} className={mainStyles.WorkerSkillsBack}>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <div className={mainStyles.Header}>&nbsp;</div>
                  <div className={mainStyles.AgentName}>Actions — Audit Log</div>
                </Grid>
              </Grid>

              <Grid container spacing={1}>
                <Grid item xs={12} md={2}>
                  <input
                    type="text"
                    placeholder="Action (e.g. workflow_updated)"
                    className={`${styles.FormControl} ${styles.FullWidth}`}
                    value={filter.action ?? ''}
                    onChange={(e) => this.handleFilter('action', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <select
                    className={`${styles.FormControl} ${styles.FullWidth}`}
                    value={filter.entity_type ?? ''}
                    onChange={(e) => this.handleFilter('entity_type', e.target.value)}
                  >
                    <option value="">(any entity)</option>
                    <option value="workflow">workflow</option>
                    <option value="queue">queue</option>
                    <option value="agent">agent</option>
                  </select>
                </Grid>
                <Grid item xs={12} md={2}>
                  <input
                    type="number"
                    placeholder="Actor ID"
                    className={`${styles.FormControl} ${styles.FullWidth}`}
                    value={filter.actor_id ?? ''}
                    onChange={(e) => this.handleFilter('actor_id', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <input
                    type="date"
                    className={`${styles.FormControl} ${styles.FullWidth}`}
                    value={filter.from ?? ''}
                    onChange={(e) => this.handleFilter('from', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <input
                    type="date"
                    className={`${styles.FormControl} ${styles.FullWidth}`}
                    value={filter.to ?? ''}
                    onChange={(e) => this.handleFilter('to', e.target.value)}
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} className={mainStyles.WorkerSkillsBack}>
              <TableContainer component={Paper}>
                <Table aria-label="audit log table">
                  <TableHead>
                    <TableRow>
                      <TableCell align="left" className={styles.ColId}>ID</TableCell>
                      <TableCell align="left" className={styles.ColDate}>Date / Time</TableCell>
                      <TableCell align="left" className={styles.ColActor}>Actor</TableCell>
                      <TableCell align="left" className={styles.ColAction}>Action</TableCell>
                      <TableCell align="left" className={styles.ColTarget}>Target</TableCell>
                      <TableCell align="left" className={styles.ColField}>Field</TableCell>
                      <TableCell align="left" className={styles.ColValue}>Old value</TableCell>
                      <TableCell align="left" className={styles.ColValue}>New value</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {entries.map((row) => {
                      const target = this.parseTarget(row.TargetUserName);
                      return (
                        <TableRow key={row.Id}>
                          <TableCell align="left" className={styles.ColId}>{row.Id}</TableCell>
                          <TableCell align="left" className={styles.ColDate}>{row.CreatedAt}</TableCell>
                          <TableCell align="left" className={styles.ColActor}>
                            <div>{row.ActorName ?? '—'}</div>
                            {row.ActorId && <div className={styles.SubText}>#{row.ActorId}</div>}
                          </TableCell>
                          <TableCell align="left" className={styles.ColAction}>
                            <span className={this.actionBadgeClass(row.Action)}>{row.Action}</span>
                          </TableCell>
                          <TableCell align="left" className={styles.ColTarget}>
                            {target ? (
                              <>
                                <span className={styles.EntityBadge}>{target.type}</span>
                                <Tooltip title={target.id} placement="top">
                                  <div className={styles.SubText}>{this.truncate(target.id, 20)}</div>
                                </Tooltip>
                              </>
                            ) : '—'}
                          </TableCell>
                          <TableCell align="left" className={styles.ColField}>
                            {row.Field ? <code className={styles.FieldCode}>{row.Field}</code> : '—'}
                          </TableCell>
                          <TableCell align="left" className={styles.ColValue}>
                            <Tooltip title={row.OldValue ?? ''} placement="top">
                              <span className={styles.ValueOld}>{this.truncate(row.OldValue, 40)}</span>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="left" className={styles.ColValue}>
                            <Tooltip title={row.NewValue ?? ''} placement="top">
                              <span className={styles.ValueNew}>{this.truncate(row.NewValue, 40)}</span>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {entries.length === 0 && !this.state.backdrop && (
                      <TableRow>
                        <TableCell colSpan={8} align="center">No records found</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {pagination !== null && (
                <Grid item xs={12}>
                  <Pagination
                    className={styles.Pagination}
                    count={pagination.LastPage}
                    page={pagination.CurrentPage}
                    onChange={(_e, page) => this.handleChangePage(page)}
                    variant="outlined"
                    shape="rounded"
                  />
                </Grid>
              )}
            </Grid>
          </Grid>
        </Container>

        <Snackbar
          open={this.state.errorAlert}
          autoHideDuration={5000}
          onClose={() => this.setState({ errorAlert: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <Alert onClose={() => this.setState({ errorAlert: false })} severity="error" variant="filled">
            Error<br />
            <div className={styles.AlertError}>{this.state.errors.join(', ')}</div>
          </Alert>
        </Snackbar>

        <Backdrop className={styles.Backdrop} open={this.state.backdrop}>
          <CircularProgress className={styles.BackdropProgress} />&nbsp;&nbsp;
          <p className={styles.BackdropProgress}>Loading...</p>
        </Backdrop>
      </div>
    );
  }
}
